import { unwrap, type components } from "@sagot/api-client";
import { toast } from "@sagot/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceId } from "../../../../session/workspace-context";

type Schemas = components["schemas"];
export type OidcSettings = Schemas["OidcSettingsOut"];
export type OidcSettingsBody = Schemas["OidcSettingsIn"];
export type OidcAgent = Schemas["OidcAgentOut"];

const STALE_MS = 30_000;

export const oidcSettingsKey = (wsId: string) => ["ws", wsId, "settings", "launch-settings", "oidc"] as const;

/** `GET /v1/launch-settings/oidc` (F20): the workspace's operator OpenID provider, never its secret, and the addresses to register. */
export function useOidcSettings() {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: oidcSettingsKey(wsId), queryFn: () => unwrap(api.GET("/v1/launch-settings/oidc")), retry: false, staleTime: STALE_MS });
}

/** `PUT /v1/launch-settings/oidc`: puts the answer in the cache and keeps the failure text for the card to show next to its draft. */
export function useSaveOidcSettings(savedMessage: string) {
  const api = useApi();
  const wsId = useWorkspaceId();
  const queryClient = useQueryClient();
  const message = useErrorMessage();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const save = async (body: OidcSettingsBody): Promise<boolean> => {
    setBusy(true);
    setFailure(null);
    try {
      const saved = await unwrap(api.PUT("/v1/launch-settings/oidc", { body }));
      queryClient.setQueryData(oidcSettingsKey(wsId), saved);
      toast.success(savedMessage);
      return true;
    } catch (caught) {
      setFailure(message(caught));
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { save, busy, failure, clearFailure: () => { setFailure(null); } };
}
